import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, X } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useQuery } from "@tanstack/react-query";
import {
  PtsCurrentUserDetailsService,
  PROFILE_VIEW_SECTIONS,
  formatProfileDisplayValue,
  UploadService,
  type CurrentUserDetailsResponse,
  type ProfileViewSection,
} from "@/services/APIs";
const EmployeeProfilePage = () => {
  const navigate = useNavigate();
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

  const { data: profileDetails, isLoading: profileLoading } = useQuery({
    queryKey: ["current-user-details", "profile"],
    queryFn: () => PtsCurrentUserDetailsService.getCurrentUserDetails(),
    // Old API: GET /formily/dynamic-model/PTS%20Emp/{employeeId}/
  });

  useEffect(() => {
    const imageId = profileDetails?.img ?? profileDetails?.image ?? null;
    if (!imageId) {
      setProfileImageUrl(null);
      return;
    }
    UploadService.getFileById({ id: imageId })
      .then(imageData => {
        const url =
          imageData?.file_path_thumbnail ?? imageData?.file_path ?? null;
        setProfileImageUrl(url);
        setImageError(false);
      })
      .catch(() => {
        setProfileImageUrl(null);
        setImageError(true);
      });
  }, [profileDetails?.img, profileDetails?.image]);

  const personalName = [
    profileDetails?.personal_details?.first_name,
    profileDetails?.personal_details?.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const displayName = (() => {
    const full = profileDetails?.full_name?.trim();
    if (full) {
      return full;
    }
    if (personalName) {
      return personalName;
    }
    return "Employee";
  })();

  const initials = (() => {
    const fromApi = profileDetails?.initials?.trim();
    if (fromApi) {
      return fromApi.toUpperCase();
    }
    const parts = displayName.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName[0]?.toUpperCase() ?? "U";
  })();

  const getSectionData = (
    details: CurrentUserDetailsResponse,
    dataKey: ProfileViewSection["dataKey"]
  ) => details[dataKey] ?? {};

  if (profileLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!profileDetails?.employee_id) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 p-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <User className="mx-auto mb-3 h-12 w-12 text-amber-500" />
          <h2 className="mb-2 text-lg font-semibold text-gray-800">
            No Employee Profile Linked
          </h2>
          <p className="text-sm text-gray-600">
            Your account is not linked to an employee profile. Please contact
            your administrator.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-4">
          <Avatar
            className="h-24 w-24 cursor-pointer rounded-lg border-4 border-white shadow-lg transition-transform hover:scale-105"
            onClick={() => {
              if (profileImageUrl && !imageError) {
                setIsImagePreviewOpen(true);
              }
            }}
            title={profileImageUrl && !imageError ? "Click to preview" : ""}
          >
            {profileImageUrl && !imageError ? (
              <AvatarImage
                src={profileImageUrl}
                alt={displayName}
                className="object-cover"
                onError={() => setImageError(true)}
              />
            ) : null}
            <AvatarFallback className="from-primary to-primary/70 text-primary-foreground bg-gradient-to-br text-2xl font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">{displayName}</h1>
            <p className="text-sm text-[#627384]">My Profile</p>
          </div>
        </div>
      </div>

      {/* Edit Profile — disabled (no edit API for current-user-details yet)
      <Button onClick={() => setIsEditMode(true)}>
        <Pencil className="h-4 w-4" />
        Edit Profile
      </Button>
      {isEditMode && (
        <Card>
          <SchemaFormView form={form} schemaJson={schemaInfo} />
          <Button onClick={handleSubmit}>Save Changes</Button>
        </Card>
      )}
      */}

      <div className="space-y-6">
        {PROFILE_VIEW_SECTIONS.map(section => {
          const sectionData = getSectionData(profileDetails, section.dataKey);

          return (
            <Card
              key={section.dataKey}
              className="border border-gray-200 bg-white shadow-sm"
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">
                  {section.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {section.fields.map(field => {
                    const rawValue =
                      sectionData[field.key as keyof typeof sectionData];
                    const displayValue = formatProfileDisplayValue(
                      rawValue,
                      field.type
                    );

                    return (
                      <div key={field.key} className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">
                          {field.label}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={isImagePreviewOpen} onOpenChange={setIsImagePreviewOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-[90vw] items-center justify-center border-none bg-transparent p-0 shadow-none sm:max-w-lg [&>button]:hidden">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute -right-2 -top-2 z-10 h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-100"
              onClick={() => setIsImagePreviewOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            {profileImageUrl && (
              <img
                src={profileImageUrl}
                alt={displayName}
                className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeProfilePage;

/*
 * Edit Profile — commented out (previous implementation)
 *
 * import { ArrowLeft, Pencil, Save } from "lucide-react";
 * import SchemaFormView from "@/components/Schema/SchemaFormView";
 * import { createForm } from "@formily/core";
 * import { useQueryClient } from "@tanstack/react-query";
 * import {
 *   SchemaService,
 *   DynamicModuleService,
 *   mapCurrentUserDetailsToProfile,
 * } from "@/services/APIs";
 * import { removeMetaFields } from "@/lib/removeMetaFields";
 * import { checkFormChanges } from "@/lib/checkFormChanges";
 * import { notify } from "@/components/common/Notification";
 * import { getUserInfo, setLocalUserInfo } from "@/lib/storeManager";
 *
 * const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
 * const [isEditMode, setIsEditMode] = useState(false);
 * const [isSubmitting, setIsSubmitting] = useState(false);
 * const form = useMemo(() => createForm({}), []);
 *
 * const handleSubmit = async () => {
 *   const response = await DynamicModuleService.updateItem(
 *     EMPLOYEE_SCHEMA_NAME,
 *     profileEmployeeId,
 *     modifiedValues
 *   );
 * };
 */
